// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";

import { isSet } from "shared/utils/utils";
import { IRegulation } from "shared/types/parameter";
import { ICloudParameter, IVirtAssign } from "shared/types/cloudtypes";
import { IAction, ActionType, raiseAction, IActionTypeSelectTariffCircuits } from "actions";
import {
  getSiteSelection,
  toDropdownItems,
  getVirtualSiteSourceName,
  renderVirtualSiteIcon,
} from "guiutils";
import { IState } from "states/states";
import { Site } from "states/sitestates";

import Dropdown from "view/comp/Dropdown";

interface IControlCircuitSelectExtProps {
  className?: string;
  isDashboard?: boolean;
}

interface IControlCircuitSelectProps {
  controlCircuitIndex: number | undefined;
  controlParameters: IRegulation[];
  virtualRegulations: IVirtAssign[];
  siteId: number | undefined;
  siteList: Site[] | undefined;
}

const mapStateToProps = (state: IState, ownProps: IControlCircuitSelectExtProps) => {
  const selection = getSiteSelection(state);
  let controlCircuitIndex = state.para.parameter?.regulation.findIndex(
    (curItem, curIndex) => (curItem.id ?? curIndex) === selection?.controlCircuitId,
  );
  if (isSet(controlCircuitIndex) && controlCircuitIndex < 0) {
    controlCircuitIndex = undefined;
  }
  if (
    !isSet(controlCircuitIndex) &&
    state.para.parameter &&
    state.para.parameter.regulation.length > 0
  ) {
    controlCircuitIndex = 0; // make default selection
  }

  const props: IControlCircuitSelectProps = {
    controlCircuitIndex,
    controlParameters: state.para.parameter?.regulation ?? [],
    virtualRegulations:
      (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.regulation ?? [],
    siteId: state.cached.selectedSite,
    siteList: state.site.siteList,
  };
  return props;
};

interface IControlCircuitSelectDispatch {
  onControlIdChanged: (controlId: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IControlCircuitSelectDispatch = {
    onControlIdChanged: (controlId: number) => {
      const payload: IActionTypeSelectTariffCircuits = { selectedIds: [controlId] };
      dispatch(raiseAction(ActionType.ACT_SELECT_MONITOR, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_user_caption: {
    id: "Select control circuit",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  virtualSite: { site: string };
}>({
  virtualSite: {
    id: "This item's data originates from {site}",
  },
});

class ControlCircuitSelectComponent extends React.Component<
  IControlCircuitSelectExtProps & IControlCircuitSelectProps & IControlCircuitSelectDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const circuitIndexes = Array.from(Array(this.props.controlParameters.length).keys()).sort(
      (a, b) =>
        this.props.controlParameters[a].name.localeCompare(this.props.controlParameters[b].name),
    );

    const controlcircuitItems = toDropdownItems(
      circuitIndexes,
      "controlId",
      (name, index) => {
        const id = this.props.controlParameters[index].id ?? index;
        this.props.onControlIdChanged(id);
      },
      (index) => this.renderRegulationLabel(index),
    );

    return (
      <div
        id="controlcircuitselect"
        className={`${this.props.className?.replaceAll("btn-header", "") ?? ""}`}
      >
        <Dropdown
          className={this.props.className}
          items={controlcircuitItems}
          caption={this.context.formatMessage(messages.msg_user_caption)}
          curItem={
            isSet(this.props.controlCircuitIndex) ?
              this.renderRegulationLabel(this.props.controlCircuitIndex)
            : this.context.formatMessage(messages.msg_user_caption)
          }
        />
      </div>
    );
  }

  private renderRegulationLabel(index: number) {
    const virtualSiteName =
      this.props.isDashboard ?
        undefined
      : getVirtualSiteSourceName(
          this.props.virtualRegulations,
          index,
          this.props.siteId,
          this.props.siteList,
        );
    return (
      <>
        {this.props.controlParameters[index]?.name}
        {virtualSiteName &&
          renderVirtualSiteIcon(
            this.context.formatMessage(valueMessages.virtualSite, { site: virtualSiteName }),
          )}
      </>
    );
  }
}

const ControlCircuitSelect = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ControlCircuitSelectComponent);

export default ControlCircuitSelect;
